import { notificationService, NotificationType } from './NotificationService';

export enum FeedbackType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  MOVE_VALID = 'move_valid',
  MOVE_INVALID = 'move_invalid',
  GAME_WIN = 'game_win',
  GAME_LOSS = 'game_loss',
  GAME_DRAW = 'game_draw',
  CONNECTION_LOST = 'connection_lost',
  CONNECTION_RESTORED = 'connection_restored'
}

export interface FeedbackOptions {
  /** Duration in milliseconds (0 for persistent) */
  duration?: number;
  /** Whether to show as notification */
  showNotification?: boolean;
  /** Whether to play sound (if enabled) */
  playSound?: boolean;
  /** Whether to trigger haptic feedback (mobile) */
  haptic?: boolean;
  /** Custom action for the feedback */
  action?: {
    label: string;
    callback: () => void;
  };
}

export interface VisualFeedbackState {
  type: FeedbackType;
  message: string;
  timestamp: Date;
  element?: HTMLElement;
}

type FeedbackListener = (feedback: VisualFeedbackState) => void;

export class FeedbackService {
  private static instance: FeedbackService;
  private listeners: FeedbackListener[] = [];
  private soundEnabled = true;
  private hapticEnabled = true;
  private feedbackQueue: VisualFeedbackState[] = [];

  private constructor() {
    this.loadSettings();
    this.setupSounds();
  }

  static getInstance(): FeedbackService {
    if (!FeedbackService.instance) {
      FeedbackService.instance = new FeedbackService();
    }
    return FeedbackService.instance;
  }

  /**
   * Show feedback for a user action
   */
  showFeedback(
    type: FeedbackType,
    message: string,
    options: FeedbackOptions = {}
  ): void {
    const {
      duration = 3000,
      showNotification = false,
      playSound = true,
      haptic = true,
      action
    } = options;

    const feedback: VisualFeedbackState = {
      type,
      message,
      timestamp: new Date()
    };

    // Add to feedback queue
    this.feedbackQueue.unshift(feedback);
    if (this.feedbackQueue.length > 10) {
      this.feedbackQueue.pop();
    }

    // Notify listeners
    this.notifyListeners(feedback);

    // Show notification if requested
    if (showNotification) {
      const notificationType = this.mapFeedbackToNotificationType(type);
      notificationService.addNotification({
        type: notificationType,
        title: this.getFeedbackTitle(type),
        message,
        duration: duration > 0 ? duration : undefined,
        persistent: duration === 0,
        actionLabel: action?.label,
        onAction: action?.callback
      });
    }

    // Play sound if enabled
    if (playSound && this.soundEnabled) {
      this.playFeedbackSound(type);
    }

    // Trigger haptic feedback if enabled and supported
    if (haptic && this.hapticEnabled) {
      this.triggerHapticFeedback(type);
    }
  }

  /**
   * Show feedback for a valid move
   */
  showMoveValidFeedback(element?: HTMLElement): void {
    this.showFeedback(FeedbackType.MOVE_VALID, 'Move placed successfully', {
      duration: 1000,
      showNotification: false,
      playSound: true,
      haptic: true
    });

    // Add visual feedback to the element
    if (element) {
      this.addElementFeedback(element, 'move-valid');
    }
  }

  /**
   * Show feedback for an invalid move
   */
  showMoveInvalidFeedback(element?: HTMLElement, reason = 'Invalid move'): void {
    this.showFeedback(FeedbackType.MOVE_INVALID, reason, {
      duration: 2000,
      showNotification: false,
      playSound: true,
      haptic: true
    });

    // Add visual feedback to the element
    if (element) {
      this.addElementFeedback(element, 'move-invalid');
    }
  }

  /**
   * Show game result feedback
   */
  showGameResultFeedback(result: 'win' | 'loss' | 'draw', opponent?: string): void {
    const type = result === 'win' ? FeedbackType.GAME_WIN : 
                 result === 'loss' ? FeedbackType.GAME_LOSS : 
                 FeedbackType.GAME_DRAW;

    const messages = {
      win: opponent ? `You won against ${opponent}!` : 'You won the game!',
      loss: opponent ? `You lost to ${opponent}` : 'You lost the game',
      draw: opponent ? `Draw with ${opponent}` : 'Game ended in a draw'
    };

    this.showFeedback(type, messages[result], {
      duration: 5000,
      showNotification: true,
      playSound: true,
      haptic: true
    });
  }

  /**
   * Show connection status feedback
   */
  showConnectionFeedback(connected: boolean): void {
    const type = connected ? FeedbackType.CONNECTION_RESTORED : FeedbackType.CONNECTION_LOST;
    const message = connected ? 'Connection restored' : 'Connection lost';

    this.showFeedback(type, message, {
      duration: connected ? 3000 : 0, // Persistent for connection lost
      showNotification: true,
      playSound: true,
      haptic: false
    });
  }

  /**
   * Show success feedback
   */
  showSuccess(message: string, options: FeedbackOptions = {}): void {
    this.showFeedback(FeedbackType.SUCCESS, message, {
      duration: 3000,
      showNotification: true,
      ...options
    });
  }

  /**
   * Show error feedback
   */
  showError(message: string, options: FeedbackOptions = {}): void {
    this.showFeedback(FeedbackType.ERROR, message, {
      duration: 5000,
      showNotification: true,
      ...options
    });
  }

  /**
   * Show warning feedback
   */
  showWarning(message: string, options: FeedbackOptions = {}): void {
    this.showFeedback(FeedbackType.WARNING, message, {
      duration: 4000,
      showNotification: true,
      ...options
    });
  }

  /**
   * Show info feedback
   */
  showInfo(message: string, options: FeedbackOptions = {}): void {
    this.showFeedback(FeedbackType.INFO, message, {
      duration: 3000,
      showNotification: false,
      ...options
    });
  }

  /**
   * Subscribe to feedback events
   */
  subscribe(listener: FeedbackListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index !== -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get recent feedback
   */
  getRecentFeedback(): VisualFeedbackState[] {
    return [...this.feedbackQueue];
  }

  /**
   * Update settings
   */
  updateSettings(settings: { soundEnabled?: boolean; hapticEnabled?: boolean }): void {
    if (typeof settings.soundEnabled === 'boolean') {
      this.soundEnabled = settings.soundEnabled;
    }
    if (typeof settings.hapticEnabled === 'boolean') {
      this.hapticEnabled = settings.hapticEnabled;
    }
    this.saveSettings();
  }

  /**
   * Get current settings
   */
  getSettings(): { soundEnabled: boolean; hapticEnabled: boolean } {
    return {
      soundEnabled: this.soundEnabled,
      hapticEnabled: this.hapticEnabled
    };
  }

  private addElementFeedback(element: HTMLElement, feedbackClass: string): void {
    // Remove any existing feedback classes
    element.classList.remove('move-valid', 'move-invalid', 'feedback-pulse');
    
    // Add the feedback class
    element.classList.add(feedbackClass, 'feedback-pulse');
    
    // Remove the classes after animation
    setTimeout(() => {
      element.classList.remove(feedbackClass, 'feedback-pulse');
    }, 600);
  }

  private mapFeedbackToNotificationType(type: FeedbackType): NotificationType {
    switch (type) {
      case FeedbackType.SUCCESS:
      case FeedbackType.MOVE_VALID:
      case FeedbackType.GAME_WIN:
      case FeedbackType.CONNECTION_RESTORED:
        return NotificationType.SUCCESS;
      case FeedbackType.ERROR:
      case FeedbackType.MOVE_INVALID:
      case FeedbackType.CONNECTION_LOST:
        return NotificationType.ERROR;
      case FeedbackType.WARNING:
        return NotificationType.WARNING;
      case FeedbackType.GAME_LOSS:
      case FeedbackType.GAME_DRAW:
        return NotificationType.GAME_RESULT;
      default:
        return NotificationType.INFO;
    }
  }

  private getFeedbackTitle(type: FeedbackType): string {
    switch (type) {
      case FeedbackType.SUCCESS:
      case FeedbackType.MOVE_VALID:
        return 'Success';
      case FeedbackType.ERROR:
      case FeedbackType.MOVE_INVALID:
        return 'Error';
      case FeedbackType.WARNING:
        return 'Warning';
      case FeedbackType.GAME_WIN:
        return 'Victory!';
      case FeedbackType.GAME_LOSS:
        return 'Game Over';
      case FeedbackType.GAME_DRAW:
        return 'Draw';
      case FeedbackType.CONNECTION_LOST:
        return 'Connection Lost';
      case FeedbackType.CONNECTION_RESTORED:
        return 'Connected';
      default:
        return 'Info';
    }
  }

  private playFeedbackSound(type: FeedbackType): void {
    if (!this.soundEnabled) return;

    try {
      // Create audio context if needed
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Generate different tones for different feedback types
      const frequency = this.getFeedbackFrequency(type);
      const duration = this.getFeedbackDuration(type);
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (error) {
      console.warn('Failed to play feedback sound:', error);
    }
  }

  private getFeedbackFrequency(type: FeedbackType): number {
    switch (type) {
      case FeedbackType.SUCCESS:
      case FeedbackType.MOVE_VALID:
      case FeedbackType.GAME_WIN:
        return 800; // Higher pitch for positive feedback
      case FeedbackType.ERROR:
      case FeedbackType.MOVE_INVALID:
      case FeedbackType.GAME_LOSS:
        return 300; // Lower pitch for negative feedback
      case FeedbackType.WARNING:
        return 600; // Medium pitch for warnings
      case FeedbackType.GAME_DRAW:
        return 500; // Neutral pitch for draws
      default:
        return 400; // Default pitch
    }
  }

  private getFeedbackDuration(type: FeedbackType): number {
    switch (type) {
      case FeedbackType.MOVE_VALID:
        return 0.1; // Quick feedback for moves
      case FeedbackType.MOVE_INVALID:
        return 0.2; // Slightly longer for errors
      case FeedbackType.GAME_WIN:
      case FeedbackType.GAME_LOSS:
      case FeedbackType.GAME_DRAW:
        return 0.5; // Longer for game results
      default:
        return 0.15; // Default duration
    }
  }

  private triggerHapticFeedback(type: FeedbackType): void {
    if (!this.hapticEnabled || !navigator.vibrate) return;

    try {
      const pattern = this.getHapticPattern(type);
      navigator.vibrate(pattern);
    } catch (error) {
      console.warn('Failed to trigger haptic feedback:', error);
    }
  }

  private getHapticPattern(type: FeedbackType): number | number[] {
    switch (type) {
      case FeedbackType.SUCCESS:
      case FeedbackType.MOVE_VALID:
        return 50; // Short single vibration
      case FeedbackType.ERROR:
      case FeedbackType.MOVE_INVALID:
        return [100, 50, 100]; // Double vibration
      case FeedbackType.GAME_WIN:
        return [100, 50, 100, 50, 100]; // Triple vibration
      case FeedbackType.GAME_LOSS:
        return 200; // Long single vibration
      case FeedbackType.WARNING:
        return [50, 100, 50]; // Short-long-short pattern
      default:
        return 75; // Default vibration
    }
  }

  private setupSounds(): void {
    // Preload audio context on user interaction
    const initAudio = () => {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContext.resume();
      } catch (error) {
        console.warn('Failed to initialize audio context:', error);
      }
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };

    document.addEventListener('click', initAudio);
    document.addEventListener('touchstart', initAudio);
  }

  private notifyListeners(feedback: VisualFeedbackState): void {
    this.listeners.forEach(listener => {
      try {
        listener(feedback);
      } catch (error) {
        console.error('Error notifying feedback listener:', error);
      }
    });
  }

  private loadSettings(): void {
    try {
      const stored = localStorage.getItem('feedback-settings');
      if (stored) {
        const settings = JSON.parse(stored);
        this.soundEnabled = settings.soundEnabled ?? true;
        this.hapticEnabled = settings.hapticEnabled ?? true;
      }
    } catch (error) {
      console.warn('Failed to load feedback settings:', error);
    }
  }

  private saveSettings(): void {
    try {
      const settings = {
        soundEnabled: this.soundEnabled,
        hapticEnabled: this.hapticEnabled
      };
      localStorage.setItem('feedback-settings', JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save feedback settings:', error);
    }
  }
}

// Export singleton instance
export const feedbackService = FeedbackService.getInstance();